import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Metadata from '../components/Metadata';
import { metadata, companyInfo, menuCard } from '../data';

export default function Home() {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [activeTab, setActiveTab] = useState('by-the-glass'); // 'by-the-glass' or 'cellar-list'

  useEffect(() => {
    if (imageLoaded) {
      setShowImage(true);
      // Trigger text animations shortly after the image starts fading in
      const timer = setTimeout(() => {
        setAnimate(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [imageLoaded]);

  useEffect(() => {
    // Fallback timer in case the image takes too long to load or fails
    const fallback = setTimeout(() => {
      setImageLoaded(true);
    }, 1500);
    return () => clearTimeout(fallback);
  }, []);

  return (
    <div className="bg-black text-sand-50 font-sans selection:bg-white selection:text-black">
      <Metadata title={metadata.home.title} description={metadata.home.description} />

      {/* Giant Minimalist Hero Area */}
      <div className="relative min-h-[90vh] sm:py-24 flex flex-col justify-center items-center border-b border-white/10 bg-black overflow-hidden">
        <div className="relative w-full aspect-[4/3] sm:absolute sm:inset-0 sm:h-full sm:w-full sm:aspect-auto overflow-hidden border-b border-white/10 sm:border-b-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/5ea68c18-23db-4ec0-b08f-b9fe5fda53ae/image.png"
            alt="La Fable Wine Pour Background"
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-[2000ms] ease-out ${
              showImage ? 'opacity-75 scale-100 filter-none' : 'opacity-0 scale-105 filter grayscale'
            }`}
          />
          {/* Softer gradient to let the image shine with high contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/15 to-black/40" />
        </div>
        
        <div className="text-center z-10 space-y-6 w-full max-w-4xl px-6 py-12 sm:py-0">
          <h1 
            className={`font-display text-6xl sm:text-8xl md:text-9xl lg:text-[10rem] font-light tracking-widest leading-none text-white select-none uppercase transition-all duration-[1400ms] delay-[100ms] ease-out ${
              animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            La Fable
          </h1>
          
          <div className="space-y-6 max-w-2xl mx-auto pt-4">
            <p 
              className={`font-serif text-lg sm:text-xl md:text-2xl font-normal text-sand-200 italic leading-tight transition-all duration-[1200ms] delay-[400ms] ease-out ${
                animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              A wine pop-up by <a href={companyInfo.laFontaineUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">La Fontaine</a>
            </p>
 
            <div 
              className={`h-[1px] w-12 bg-white/20 my-4 mx-auto transition-all duration-[1200ms] delay-[600ms] ease-out ${
                animate ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`} 
            />
 
            <p 
              className={`font-sans text-xs sm:text-sm md:text-base font-normal text-sand-200/80 leading-relaxed transition-all duration-[1200ms] delay-[800ms] ease-out ${
                animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              We're building the permanent location below the club - stay tuned!
            </p>
 
            {/* Minimalist Navigation Menu */}
            <div className="mt-12 sm:mt-16 max-w-3xl mx-auto">
              <nav className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4">
                <span className={`inline-block transition-all duration-[1000ms] delay-[1000ms] ease-out ${
                  animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <Link
                    to="/the-wines"
                    className="group text-xl sm:text-2xl font-medium tracking-wide text-sand-200 hover:text-white transition-colors duration-300"
                  >
                    The wines
                  </Link>
                </span>
                <span className={`text-white/20 hidden sm:inline transition-all duration-[1000ms] delay-[1100ms] ease-out ${
                  animate ? 'opacity-20' : 'opacity-0'
                }`}>•</span>
                
                <span className={`inline-block transition-all duration-[1000ms] delay-[1200ms] ease-out ${
                  animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <Link
                    to="/the-cocktails"
                    className="group text-xl sm:text-2xl font-medium tracking-wide text-sand-200 hover:text-white transition-colors duration-300"
                  >
                    The cocktails
                  </Link>
                </span>
                <span className={`text-white/20 hidden sm:inline transition-all duration-[1000ms] delay-[1300ms] ease-out ${
                  animate ? 'opacity-20' : 'opacity-0'
                }`}>•</span>
                
                <span className={`inline-block transition-all duration-[1000ms] delay-[1400ms] ease-out ${
                  animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <Link
                    to="/the-family"
                    className="group text-xl sm:text-2xl font-medium tracking-wide text-sand-200 hover:text-white transition-colors duration-300"
                  >
                    The family
                  </Link>
                </span>
                <span className={`text-white/20 hidden sm:inline transition-all duration-[1000ms] delay-[1500ms] ease-out ${
                  animate ? 'opacity-20' : 'opacity-0'
                }`}>•</span>
                
                <span className={`inline-block transition-all duration-[1000ms] delay-[1600ms] ease-out ${
                  animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <Link
                    to="/visit-us"
                    className="group text-xl sm:text-2xl font-medium tracking-wide text-sand-200 hover:text-white transition-colors duration-300"
                  >
                    Visit us
                  </Link>
                </span>
              </nav>
            </div>
          </div>
        </div>
 
        {/* Scroll Indicator */}
        <div 
          className={`absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 transition-all duration-1000 delay-[1800ms] ease-out ${
            animate ? 'opacity-60 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="font-display text-[10px] uppercase tracking-[0.25em] text-sand-200">Scroll to explore wine list</span>
          <i className="fa-light fa-chevron-down text-xs animate-bounce" />
        </div>
      </div>

      {/* Integrated Wine List Section */}
      <section className="py-24 bg-[#fbf9f6] border-b border-neutral-200">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center space-y-4 mb-16">
            <span className="font-sans text-xs tracking-[0.2em] uppercase text-neutral-700 block font-normal">
              Curated by Davide Piccolo
            </span>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black text-neutral-900 tracking-tighter uppercase">
              The Wine List
            </h2>
            <p className="font-sans text-sm sm:text-base font-normal text-neutral-700 max-w-xl mx-auto leading-relaxed">
              Explore our selection of exceptional wines by the glass and exclusive bottles from our cellar list.
            </p>
            <div className="w-12 h-[1px] bg-neutral-300 mx-auto my-4" />
            
            {/* Tab Switcher */}
            <div className="flex justify-center gap-4 pt-6">
              <button
                onClick={() => setActiveTab('by-the-glass')}
                className={`px-6 py-3 text-xs uppercase tracking-[0.2em] transition-all duration-300 border rounded-none ${
                  activeTab === 'by-the-glass'
                    ? 'bg-black text-white border-black font-medium'
                    : 'bg-transparent text-neutral-600 border-neutral-300 hover:border-neutral-800'
                }`}
              >
                Wine by the Glass
              </button>
              <button
                onClick={() => setActiveTab('cellar-list')}
                className={`px-6 py-3 text-xs uppercase tracking-[0.2em] transition-all duration-300 border rounded-none ${
                  activeTab === 'cellar-list'
                    ? 'bg-black text-white border-black font-medium'
                    : 'bg-transparent text-neutral-600 border-neutral-300 hover:border-neutral-800'
                }`}
              >
                Cellar List
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'by-the-glass' ? (
            <div className="space-y-16 max-w-5xl mx-auto">
              {menuCard.wines.map((category, idx) => (
                <div key={idx} className="space-y-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-display text-sm uppercase tracking-[0.25em] text-neutral-900 font-bold">
                      {category.category}
                    </span>
                    <div className="flex-1 h-[1px] bg-neutral-200" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {category.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="group space-y-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-display text-base sm:text-lg text-neutral-900 font-bold tracking-tight group-hover:text-neutral-700 transition-colors">
                            {item.name}
                          </span>
                          <div className="flex-1 border-b border-dotted border-neutral-300 mx-2 self-end mb-1.5 min-w-4" />
                          <span className="font-display text-sm sm:text-base font-bold text-neutral-900 whitespace-nowrap shrink-0">
                            {item.price}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between text-xs text-neutral-600 font-normal">
                          <span className="font-sans uppercase tracking-wider text-[10px]">
                            {item.producer}
                          </span>
                        </div>
                        {item.desc && (
                          <p className="font-sans text-xs sm:text-sm font-normal text-neutral-700 leading-relaxed pt-1">
                            {item.desc}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-16 max-w-5xl mx-auto">
              {menuCard.exclusiveWines.map((category, idx) => (
                <div key={idx} className="space-y-6">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-display text-sm uppercase tracking-[0.25em] text-neutral-900 font-bold">
                      {category.category}
                    </span>
                    <div className="flex-1 h-[1px] bg-neutral-200" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {category.items.slice(0, 8).map((item, itemIdx) => (
                      <div key={itemIdx} className="group space-y-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-display text-base sm:text-lg text-neutral-900 font-bold tracking-tight group-hover:text-neutral-700 transition-colors">
                            {item.name}
                          </span>
                          <div className="flex-1 border-b border-dotted border-neutral-300 mx-2 self-end mb-1.5 min-w-4" />
                          <span className="font-display text-sm sm:text-base font-bold text-neutral-900 whitespace-nowrap shrink-0">
                            {item.price}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between text-xs text-neutral-600 font-normal">
                          <span className="font-sans uppercase tracking-wider text-[10px]">
                            {item.producer}
                          </span>
                        </div>
                        {item.desc && (
                          <p className="font-sans text-xs sm:text-sm font-normal text-neutral-700 leading-relaxed pt-1">
                            {item.desc}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="text-center pt-8">
                <Link
                  to="/the-wines"
                  className="px-8 py-4 border border-black text-black text-xs uppercase tracking-[0.25em] hover:bg-black hover:text-white transition-all duration-300 inline-flex items-center gap-3 font-medium rounded-none"
                >
                  View Full Cellar List
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </Link>
              </div>
            </div>
          )}

          {/* PDF Downloads & Sommelier Info */}
          <div className="mt-20 border-t border-neutral-200 pt-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <h3 className="font-display text-xl text-neutral-900 uppercase tracking-wider">
                Download Full Wine Lists
              </h3>
              <p className="font-sans text-xs sm:text-sm font-normal text-neutral-700 leading-relaxed">
                Prefer to view our lists offline? Download the complete, up-to-date wine lists compiled by Davide Piccolo.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <a
                  href="https://assets.auxodev.dk/assets/5ea68c18-23db-4ec0-b08f-b9fe5fda53ae/wine-by-the-glass-aug-2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 border border-neutral-300 bg-white text-neutral-800 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-all duration-300 inline-flex items-center justify-center gap-2 rounded-none"
                >
                  <i className="fa-solid fa-file-pdf text-xs" />
                  Wine by the glass
                </a>
                <a
                  href="https://assets.auxodev.dk/assets/5ea68c18-23db-4ec0-b08f-b9fe5fda53ae/cellar-list-aug-2026.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-3 border border-neutral-300 bg-white text-neutral-800 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-all duration-300 inline-flex items-center justify-center gap-2 rounded-none"
                >
                  <i className="fa-solid fa-file-pdf text-xs" />
                  Cellar list
                </a>
              </div>
            </div>

            <div className="border border-neutral-200 p-6 bg-white/60 backdrop-blur-sm space-y-4">
              <span className="font-display text-[10px] tracking-[0.25em] uppercase text-neutral-500 block">
                Our Philosophy
              </span>
              <p className="font-sans text-xs sm:text-sm font-normal text-neutral-700 leading-relaxed">
                Our wine list has been put together by our sommelier and Bar Manager, Davide Piccolo, holder of the WSET Level 4 Diploma.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
